import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Seo } from "@/components/Seo";
import { UrgenceBanner } from "@/components/UrgenceBanner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { MobileDevisButton } from "@/components/MobileDevisButton";
import HomePage from "@/pages/HomePage";
import OccasionsPage from "@/pages/OccasionsPage";
import DepotVentePage from "@/pages/DepotVentePage";
import PneusPage from "@/pages/PneusPage";
import VehiculePretPage from "@/pages/VehiculePretPage";
import LocationVehiculePage from "@/pages/LocationVehiculePage";
import ConvoyagePage from "@/pages/ConvoyagePage";
import PolitiqueConfidentialitePage from "@/pages/PolitiqueConfidentialitePage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";

const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, anchors: true });
    window.__lenis = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView();
      }, 80);
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollManager />
        <div className="min-h-screen bg-background text-foreground">
          <UrgenceBanner />
          <Header />
          <main>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Seo
                      title="MECA AUTO CLEAN 01 — Garage automobile à Pont-d'Ain"
                      description="Garage automobile à Pont-d'Ain : réparation mécanique, entretien courant, diagnostic, montage pneus et dépôt-vente. Devis gratuit au 07 56 95 10 07."
                    />
                    <HomePage />
                  </>
                }
              />
              <Route
                path="/occasions"
                element={
                  <>
                    <Seo
                      title="Véhicules d'occasion à Pont-d'Ain | MECA AUTO CLEAN 01"
                      description="Vente de véhicules d'occasion à Pont-d'Ain : marque, modèle, année, kilométrage, équipements et prix. Contactez MECA AUTO CLEAN 01 pour les disponibilités."
                    />
                    <OccasionsPage />
                  </>
                }
              />
              <Route
                path="/depot-vente"
                element={
                  <>
                    <Seo
                      title="Dépôt-vente automobile à Pont-d'Ain | MECA AUTO CLEAN 01"
                      description="Vendez votre voiture en dépôt-vente avec MECA AUTO CLEAN 01 à Pont-d'Ain : présentation du véhicule, modalités et conditions de commercialisation définies ensemble."
                    />
                    <DepotVentePage />
                  </>
                }
              />
              <Route
                path="/pneus"
                element={
                  <>
                    <Seo
                      title="Montage pneus à Pont-d'Ain | MECA AUTO CLEAN 01"
                      description="Montage pneumatique, équilibrage, permutation et réparation crevaison à Pont-d'Ain. Station de montage partenaire Centralepneus.fr."
                    />
                    <PneusPage />
                  </>
                }
              />
              <Route
                path="/vehicule-de-pret"
                element={
                  <>
                    <Seo
                      title="Véhicule de prêt à Pont-d'Ain | MECA AUTO CLEAN 01"
                      description="Véhicule de prêt pendant la réparation de votre voiture à Pont-d'Ain : permis de conduire, carte d'identité et justificatif de domicile à joindre en ligne."
                    />
                    <VehiculePretPage />
                  </>
                }
              />
              <Route
                path="/location-de-vehicule"
                element={
                  <>
                    <Seo
                      title="Location de véhicule à Pont-d'Ain | MECA AUTO CLEAN 01"
                      description="Location de véhicule à Pont-d'Ain sur devis : constituez votre dossier en ligne avec permis de conduire, carte d'identité et justificatif de domicile."
                    />
                    <LocationVehiculePage />
                  </>
                }
              />
              <Route
                path="/convoyage"
                element={
                  <>
                    <Seo
                      title="Convoyage automobile | MECA AUTO CLEAN 01"
                      description="Convoyage de véhicule par MECA AUTO CLEAN 01 : achat ou vente à distance, déménagement, rapatriement. Demandez un devis pour votre trajet."
                    />
                    <ConvoyagePage />
                  </>
                }
              />
              <Route
                path="/politique-de-confidentialite"
                element={
                  <>
                    <Seo
                      title="Politique de confidentialité | MECA AUTO CLEAN 01"
                      description="Politique de confidentialité du garage MECA AUTO CLEAN 01 à Pont-d'Ain : données collectées, finalités, droits RGPD et cookies."
                    />
                    <PolitiqueConfidentialitePage />
                  </>
                }
              />
              <Route
                path="/mentions-legales"
                element={
                  <>
                    <Seo
                      title="Mentions légales | MECA AUTO CLEAN 01"
                      description="Mentions légales du site du garage MECA AUTO CLEAN 01, 53 rue du Premier Septembre 1944, 01160 Pont-d'Ain."
                    />
                    <MentionsLegalesPage />
                  </>
                }
              />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </main>
          <Footer />
          <MobileDevisButton />
          <CookieBanner />
          <Toaster richColors closeButton position="top-center" />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
